import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import isString from "lodash/isString";
import { useMemo, useRef } from "react";

const SelectMultipleInput = ({
  value = [],
  onChange,
  fullWidth = true,
  sx,
  options,
  size = "small",
  onBlur,
  label,
  placeholder,
  ChipProps,
  ...rest
}) => {
  const inputRef = useRef(null); // Ref for focusing the input

  // Coerce non-array `value` (boolean, scalar string/number, null, …) to []
  // so a stale data shape upstream doesn't crash the input. The form's
  // submit will replace the bad value with a real array on save. This
  // happens when a field that is sometimes-array sometimes-scalar (e.g.
  // borrower.mortgage_lates landing as `true` in legacy records) is wired
  // to a multi-select.
  const selectedOptions = useMemo(() => {
    const safeValue = Array.isArray(value) ? value : [];
    return options.filter((option) =>
      isString(option)
        ? safeValue.includes(option)
        : safeValue.includes(option.value),
    );
  }, [value, options]);

  const styles = useMemo(() => {
    return {
      ".MuiInputBase-root": {
        gap: "5px",
      },
      ".MuiAutocomplete-tag": { margin: "0" },
      ".MuiAutocomplete-endAdornment": { marginRight: "5px" },
      ".MuiChip-root": {
        marginTop: 0,
        marginBottom: 0,
        ".MuiChip-label": {
          fontSize: "11.8px",
          fontWeight: 700,
          paddingLeft: "12px",
          paddingRight: "12px",
          color: "#33475b",
        },
      },
      ...sx,
    };
  }, [sx]);

  const emptyPlacholderValue = useMemo(() => {
    if (placeholder) {
      return placeholder;
    }
    return "Start Typing...";
  }, [placeholder]);


  return (
    <Autocomplete
      multiple
      onBlur={onBlur}
      value={selectedOptions}
      fullWidth={fullWidth}
      size={size}
      options={options}
      sx={styles}
      onChange={(event, newValue) => {
        const selectedValues = newValue.map((option) =>
          isString(option) ? option : option.value,
        );
        onChange(selectedValues);

        // Set focus back to the input when a chip is deleted
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }}
      isOptionEqualToValue={(option, value) => {
        return isString(option) ? option === value : option.value === value;
      }}
      getOptionLabel={(option) => (isString(option) ? option : option.label)}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        if (isString(option)) {
          return (
            <Box key={option} {...optionProps}>
              {option}
            </Box>
          );
        }

        return (
          <Box sx={{ fontSize: ".7rem" }} key={option.value} {...optionProps}>
            {option.label}
          </Box>
        );
      }}
      ChipProps={ChipProps}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth={fullWidth}
          placeholder={emptyPlacholderValue}
          inputRef={inputRef} // Attach ref to input field
          endAdornment={params.InputProps.endAdornment}
          inputProps={{
            ...params.inputProps,
          }}
        />
      )}
    />
  );
};

export default SelectMultipleInput;
